/* maven-qty — the cart calls and the serving count control, shared.
 *
 * Heidi, 2026-09-14: a dish's Add button turns into a serving count that goes
 * up, goes down and takes a typed number. First on the menu cards, then "add
 * the serving count to the product page too". One implementation for both, so
 * the two cannot drift. Loaded by sections/maven-menu.liquid and by
 * sections/maven-product-qty.liquid; loading it twice is harmless.
 *
 * ⚠️ THE COUNT IS THE DISH'S OWN CART LINES, NOT ITS MEALS. It reads and writes
 * the lines for one variant that have NO `_Meal bundle` property. A meal built
 * in the menu's Choose sides tray is separate lines, and this never changes one.
 *
 * ⚠️ "LINES", PLURAL, SINCE 2026-10-08. Each entrée's "Meal bundle" automatic
 * discount covers part of a side's quantity, and Shopify then splits that side
 * into two lines (MAV-909), for example 1 discounted and 2 at full price. Until
 * 2026-10-08 this read and changed only the FIRST such line. A customer wrote
 * in that the count "does not accurately indicate how many items are in the
 * cart ... sometimes just tosses things out or adds more of the item". So the
 * count is the sum of every plain line for the variant. A raise adds the
 * difference, and a cut takes it from the last lines first.
 *
 * ⚠️ AND A SIDE THAT IS IN A MEAL SAYS SO. The same customer built meals and
 * then added the dish again, because its card still said "Add". A side's card
 * now shows "Already N in cart" under the control (Heidi's wording, 2026-10-08;
 * it read "Already in N meals" for its first hours). An entrée's card does not,
 * because its "Choose sides" button already reads "In cart: N meals".
 *
 * ⚠️ ORDERING FLOW. Kumquat's mixed-cart guard is not on Dawn (MAV-634, issue
 * #147). The product page's own Add to cart already writes to the cart without
 * it; this control does not widen that. The menu's use of it stays behind
 * scripts/mock-guard.mjs, because the menu's Add buttons are part of the mock.
 */
(function () {
  'use strict';
  if (window.MavenCart) return;

  var BUNDLE_PROP = '_Meal bundle';
  var MAX_QTY = 99;
  var listeners = [];

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok) throw new Error(json.description || json.message || 'The cart did not accept that');
        return json;
      });
    });
  }

  /* Refresh the header count, and tell every listener. */
  function apply(cart) {
    badge(cart.item_count);
    listeners.forEach(function (fn) { fn(cart); });
    return cart;
  }

  /* Read the cart, then apply it. */
  function read() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(apply);
  }

  /* Dawn's pub/sub, so the tip line and anything else listening re-reads.
   *
   * ⚠️ `typeof PUB_SUB_EVENTS`, NEVER `window.PUB_SUB_EVENTS`. Dawn's
   * assets/constants.js declares it with `const`, which is global but is NOT a
   * property of window. The window form was always undefined, so until
   * 2026-09-14 this published nothing and the tip block never re-read after a
   * change from the menu or the cart list. */
  function changed(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'maven-qty', cartData: cart });
    }
    return cart;
  }

  /* /cart/add.js answers with the added items only, so an add reads the cart.
   * /cart/update.js and /cart/change.js answer with the whole cart, so they use
   * that answer and save a second request. Before 2026-10-08 every change made
   * both requests, one after the other, and the count lagged. */
  function add(items) { return post('/cart/add.js', { items: items }).then(read).then(changed); }
  /* `updates` is { lineKey: quantity }; 0 removes the line. */
  function update(updates) { return post('/cart/update.js', { updates: updates }).then(apply).then(changed); }
  function change(key, qty) { return post('/cart/change.js', { id: key, quantity: qty }).then(apply).then(changed); }

  /* The header count is rendered from cart.item_count at request time
   * (sections/maven-header.liquid), so it goes stale after an add without this.
   * Same markup the header renders: a hidden badge and the count in the label. */
  function badge(count) {
    [].forEach.call(document.querySelectorAll('a.cart[href$="/cart"]'), function (link) {
      var el = link.querySelector('.badge');
      if (count > 0) {
        if (!el) {
          el = document.createElement('span');
          el.className = 'badge';
          el.setAttribute('aria-hidden', 'true');
          link.appendChild(el);
        }
        el.textContent = count;
        link.setAttribute('aria-label', 'Cart, ' + count + ' item' + (count === 1 ? '' : 's'));
      } else {
        if (el) el.remove();
        link.setAttribute('aria-label', 'Cart');
      }
    });
  }

  function announce(text) {
    var live = document.querySelector('.mv-cart-status');
    if (!live) {
      live = document.createElement('p');
      live.className = 'mv-cart-status visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = text;
  }

  /* An "Add" button that becomes minus, a number field and plus once the dish
   * is in the cart.
   *
   *   opts.variantId  the variant to count
   *   opts.name       the dish name, for labels and announcements
   *   opts.addClass   classes for the Add button (default: the menu card's)
   *   opts.addHtml    the Add button's content
   *   opts.modifier   an extra class on the wrapper, for placement styling
   *
   * The control does not read the cart itself; call MavenCart.read() once the
   * page has built its controls. */
  function quantityControl(opts) {
    var name = opts.name || 'this dish';
    var variantId = String(opts.variantId);

    var wrap = document.createElement('div');
    wrap.className = 'mv-dish__buy' + (opts.modifier ? ' ' + opts.modifier : '');

    var b = document.createElement('button');
    b.type = 'button';
    b.className = opts.addClass || 'mv-dish__add';
    b.setAttribute('aria-label', 'Add ' + name + ' to cart');
    b.innerHTML = opts.addHtml || '<span class="mv-dish__add-plus" aria-hidden="true">+</span> Add';

    var step = document.createElement('div');
    step.className = 'mv-dish__qty';
    step.setAttribute('role', 'group');
    step.setAttribute('aria-label', name + ', servings in cart');
    step.hidden = true;
    step.innerHTML =
      '<button type="button" class="mv-dish__qty-btn" data-qty-minus>&minus;</button>' +
      '<input class="mv-dish__qty-input" type="number" inputmode="numeric" min="0" max="' + MAX_QTY + '" step="1" value="0">' +
      '<button type="button" class="mv-dish__qty-btn" data-qty-plus>+</button>';
    var minus = step.querySelector('[data-qty-minus]');
    var plus = step.querySelector('[data-qty-plus]');
    var input = step.querySelector('input');
    minus.setAttribute('aria-label', 'One fewer ' + name);
    plus.setAttribute('aria-label', 'One more ' + name);
    input.setAttribute('aria-label', name + ' servings');

    /* "Already N in cart", for a side that is in a meal from the tray. N is
     * every serving of the side in the cart: on its own plus inside meals
     * (Heidi, 2026-10-08: "show the total"). It shows only while the side is
     * in a meal, because otherwise the control above already says it. */
    var inMeals = document.createElement('p');
    inMeals.className = 'mv-dish__inmeals';
    inMeals.hidden = true;

    wrap.appendChild(b);
    wrap.appendChild(step);
    wrap.appendChild(inMeals);

    /* What the cart holds for this dish outside any meal: every plain line, in
     * cart order, and their total. */
    var line = { lines: [], qty: 0 };
    /* pending  wanted and not yet sent
     * inflight sent and not yet confirmed by a cart read
     * A tap counts from the newest of these, never from the last confirmed
     * quantity alone, or a tap during a slow request is lost. */
    var pending = null, inflight = null, timer = null, busy = false;
    function current() {
      if (pending !== null) return pending;
      if (inflight !== null) return inflight;
      return line.qty;
    }

    /* `force` writes the field even while the customer is in it: after a
     * committed entry, so a clamped or reverted number is what they see. A
     * cart read never forces, so it cannot overwrite digits mid-typing. */
    function show(qty, force) {
      var inCart = qty > 0;
      var focusInside = step.contains(document.activeElement);
      b.hidden = inCart;
      step.hidden = !inCart;
      if (force || document.activeElement !== input) input.value = qty;
      /* Hiding the control drops focus to the page. Put it on Add instead,
       * decided before the hide, because after it nothing is focused. */
      if (!inCart && focusInside) b.focus();
      minus.setAttribute('aria-label', qty === 1 ? 'Remove ' + name + ' from cart' : 'One fewer ' + name);
      plus.disabled = qty >= MAX_QTY;
    }

    listeners.push(function (cart) {
      var lines = [], qty = 0, meals = 0;
      cart.items.forEach(function (item) {
        if (String(item.variant_id) !== variantId) return;
        var meal = item.properties && item.properties[BUNDLE_PROP];
        if (!meal) {
          lines.push({ key: item.key, qty: item.quantity });
          qty += item.quantity;
        } else if (meal !== item.handle) {
          /* A side in a meal. The meal's entrée line carries its own handle. */
          meals += item.quantity;
        }
      });
      line = { lines: lines, qty: qty };
      inMeals.textContent = 'Already ' + (qty + meals) + ' in cart';
      inMeals.hidden = meals === 0;
      if (!busy && pending === null && inflight === null) show(line.qty);
    });

    /* Send the latest wanted quantity once the clicking stops. Rapid presses
     * change the number at once and reach the cart as one request. */
    function want(qty) {
      qty = Math.max(0, Math.min(MAX_QTY, Math.round(qty)));
      pending = qty;
      show(qty, true);
      clearTimeout(timer);
      timer = setTimeout(flush, 350);
    }
    function flush() {
      if (busy || pending === null) return;
      var qty = pending;
      pending = null;
      if (qty === line.qty) { show(line.qty); return; }
      inflight = qty;
      busy = true;
      step.setAttribute('aria-busy', 'true');
      var request;
      if (qty > line.qty) {
        request = add([{ id: Number(variantId), quantity: qty - line.qty }]);
      } else {
        /* Take the cut from the last lines first. Shopify decides again which
         * part of a split line is discounted after every change, so the order
         * changes only which key is sent, not the price. */
        var updates = {}, drop = line.qty - qty;
        for (var i = line.lines.length - 1; i >= 0 && drop > 0; i--) {
          var take = Math.min(drop, line.lines[i].qty);
          updates[line.lines[i].key] = line.lines[i].qty - take;
          drop -= take;
        }
        request = update(updates);
      }
      request.then(function () {
        announce(qty === 0 ? name + ' removed from cart' : name + ': ' + qty + ' in cart');
      }).catch(function (err) {
        announce('The cart did not change. ' + err.message);
        return read();
      }).then(function () {
        busy = false;
        inflight = null;
        step.removeAttribute('aria-busy');
        if (pending !== null) flush(); else show(line.qty);
      });
    }

    b.addEventListener('click', function () {
      want(1);
      /* Focus follows the control the customer is now using. */
      setTimeout(function () { plus.focus(); }, 0);
    });
    minus.addEventListener('click', function () { want(current() - 1); });
    plus.addEventListener('click', function () { want(current() + 1); });
    /* Typing: commit on Enter or on leaving the field, not on every keystroke,
     * so "12" does not add 1 and then 12. An empty or invalid entry reverts. */
    function commitTyped() {
      var n = parseInt(input.value, 10);
      if (isNaN(n)) { show(current(), true); return; }
      want(n);
    }
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); commitTyped(); }
    });
    input.addEventListener('change', commitTyped);

    return wrap;
  }

  window.MavenCart = {
    BUNDLE_PROP: BUNDLE_PROP,
    MAX_QTY: MAX_QTY,
    post: post,
    read: read,
    onChange: function (fn) { listeners.push(fn); },
    add: add,
    update: update,
    change: change,
    announce: announce,
    quantityControl: quantityControl
  };
}());
